import React, { useState, SyntheticEvent } from "react";
import { useTheme } from "@mui/material/styles";
import { useQuery, useQueryClient } from "react-query";
import { getUser, postOTSM } from "../../api/SettingsApi";
import { useNavigate, useLocation } from "react-router-dom";

function useSettingsSelector() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { userId, previous } = location.state;

  const { data, isFetching } = useQuery(
    "UserAccount",
    async () => {
      const response = await getUser(userId);

      setFixedMargin(response.data.fixedMargin || "");
      setMarkup(response.data.fixedMarkup || "");

      if (response.data.otsm) {
        setOtsm(true);
        setOtsmDefault(response.data.otsmDefault === "Fixed Markup");
      }

      setDeliveryMarkup(response.data.deliveryMarkup ? "Yes" : "No");
      setCrateFreightMarkup(response.data.crateFreightMarkup ? "Yes" : "No");

      return response.data;
    },
    { refetchOnWindowFocus: false }
  );

  const queryClient = useQueryClient();

  const [fixedMargin, setFixedMargin] = useState("");

  const [markup, setMarkup] = useState("");

  const [err, setErr] = useState("");

  const [otsm, setOtsm] = useState(false);

  const [otsmDefault, setOtsmDefault] = useState(false);

  const [value, setValue] = useState(0);

  const [success, setSuccess] = useState("");

  const [deliveryMarkup, setDeliveryMarkup] = useState("No");

  const [crateFreightMarkup, setCrateFreightMarkup] = useState("No");

  const changeDeliveryMarkup = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setDeliveryMarkup(value);
  };

  const changeCrateFreightMarkup = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setCrateFreightMarkup(value);
  };

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
    data.otsm && setOtsm(true);
    data.fixedMargin && setFixedMargin(data.fixedMargin);
    data.fixedMarkup && setMarkup(data.fixedMarkup);
    success && setSuccess("");
  };

  const handleClick = async () => {
    if (otsm) {
      if (!fixedMargin && !markup) {
        return setSuccess(
          "Fixed Margin or Markup is Required for Over the Shoulder Mode."
        );
      }

      if (!otsmDefault && !fixedMargin) {
        return setSuccess("Fixed Margin is Required.");
      }

      if (otsmDefault && !markup) {
        return setSuccess("Fixed Markup is Required.");
      }
    }

    const res = await postOTSM({
      fixedMargin,
      fixedMarkup: markup,
      otsm,
      otsmDefault,
    });

    if (res.data === "Settings Updated") {
      queryClient.invalidateQueries("UserAccount");
      setSuccess("Your Settings Have Been Updated.");
      return queryClient.invalidateQueries("Dealer");
    }
  };

  const returnToPreviousPage = () => {
    const user = JSON.parse(
      sessionStorage.getItem("user") || JSON.stringify("")
    );
    const backRoute = previous || "dealerZone";
    navigate(`/${backRoute}/${user}`, {
      state: { ...location.state },
    });
  };

  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleClickRemoveMargin = () => {
    setFixedMargin("");
    setSuccess("");
    if (!markup && otsm) setOtsm(false);
  };

  const handleClickRemoveMarkup = () => {
    setMarkup("");
    setSuccess("");
    if (!fixedMargin && otsm) setOtsm(false);
  };

  return {
    handleClickRemoveMarkup,
    handleClickRemoveMargin,
    handleDrawerToggle,
    mobileOpen,
    returnToPreviousPage,
    handleClick,
    handleChange,
    changeDeliveryMarkup,
    changeCrateFreightMarkup,
    deliveryMarkup,
    crateFreightMarkup,
    success,
    value,
    otsmDefault,
    otsm,
    err,
    setErr,
    markup,
    fixedMargin,
    data,
    isFetching,
    previous,
    theme,
    setSuccess,
    setFixedMargin,
    setOtsm,
    setOtsmDefault,
    setMarkup,
  };
}

export default useSettingsSelector;
